<template>
  <div class="quotes-container">
    <!-- SearchBar -->
    <div class="search-section">
      <div class="search-container">
        <el-row :gutter="10">
          <el-col :span="6">
            <el-input 
              placeholder="Search Quote ID" 
              v-model="searchTip.quoteId" 
              @clear="getQuotes()"
              @keyup.enter.native="getQuotes()"
              clearable
              size="medium">
              <el-button slot="append" icon="el-icon-search" @click="getQuotes()"></el-button>
            </el-input>
          </el-col>
          <el-col :span="6">
            <el-input 
              placeholder="Search Content" 
              prefix-icon="el-icon-document" 
              v-model="searchTip.content" 
              @clear="getQuotes()"
              @keyup.enter.native="getQuotes()"
              clearable
              size="medium">
            </el-input>
          </el-col>
          <el-col :span="6">
            <el-input 
              placeholder="Search Author" 
              prefix-icon="el-icon-user" 
              v-model="searchTip.author" 
              @clear="getQuotes()"
              @keyup.enter.native="getQuotes()"
              clearable
              size="medium">
            </el-input>
          </el-col>
          <el-col :span="2">
            <el-button type="primary" @click="adding()" size="medium">
              <i class="el-icon-plus"></i> 新增
            </el-button>
          </el-col>
        </el-row>
      </div>
    </div>

    <!-- Table -->
    <div class="table-container">
      <el-table 
        :data="quoteList" 
        border 
        stripe 
        :header-cell-class-name="'headerBg'"
        class="custom-table"
        style="width: 100%">
        <el-table-column type="expand" label="#" width="40">
          <template slot-scope="scope">
            <div class="expand-content">
              <div class="expand-item">
                <span class="expand-label">创建时间：</span>
                <span class="expand-value">{{ scope.row.created_at }}</span>
              </div>
              <div class="expand-item">
                <span class="expand-label">更新时间：</span>
                <span class="expand-value">{{ scope.row.updated_at }}</span>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="quote_id" label="Quote ID" width="120" align="center"></el-table-column>
        <el-table-column prop="content" label="Content" :align="lang === 'ug' ? 'right' : 'left'">
          <template slot-scope="scope">
            <div class="content-cell" :class="lang === 'ug' ? 'ugText' : 'normal'">
              {{ scope.row.content }}
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="author" label="Author" width="200" :align="lang === 'ug' ? 'right' : 'left'">
          <template slot-scope="scope">
            <div class="author-cell" :class="lang === 'ug' ? 'ugText' : 'normal'">
              {{ scope.row.author }}
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="topic" label="Topic" width="100" align="center">
          <template slot-scope="scope">
            <el-tag type="primary" disable-transitions>{{ scope.row['topic_' + lang] }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" align="center">
          <template slot-scope="scope">
            <div class="action-buttons">
              <el-button type="success" size="small" @click="editing(scope.row)">
                <i class="el-icon-edit"></i> 编辑
              </el-button>
              <el-popconfirm 
                class="ml-5" 
                confirm-button-text='确定' 
                cancel-button-text='我再想想' 
                icon="el-icon-info" 
                icon-color="red" 
                title="您确定删除吗？" 
                @confirm="deleting(scope.row.quote_id)">
                <el-button type="danger" size="small" slot="reference">
                  <i class="el-icon-remove-outline"></i> 删除
                </el-button>
              </el-popconfirm>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- Pagination -->
      <div class="pagination-wrapper">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pageNum"
          :page-sizes="[10, 20, 30, 50]"
          :page-size="pageSize"
          :layout="isMobile ? 'prev, pager, next' : 'total, sizes, prev, pager, next, jumper'"
          :total="total">
        </el-pagination>
      </div>
    </div>

    <!-- EditBox -->
    <el-dialog 
      title="Quote" 
      :visible.sync="dialogFormVisible" 
      :width="isMobile ? '90%' : '40%'"
      class="custom-dialog"
      :fullscreen="false"
      :close-on-click-modal="false"
      custom-class="mobile-dialog">
      <el-form label-width="80px" size="small" class="edit-form">
        <el-form-item label="Quote ID">
          <el-input v-model="form.quote_id" autocomplete="off"></el-input>
        </el-form-item>
        <el-form-item label="Content">
          <el-input 
            v-model="form.content" 
            autocomplete="off" 
            type="textarea" 
            :rows="isMobile ? 4 : 3" 
            :class="lang === 'ug' ? 'ugText' : 'normal'">
          </el-input>
        </el-form-item>
        <el-form-item label="Author">
          <el-input v-model="form.author" autocomplete="off"></el-input>
        </el-form-item>
        <el-form-item label="Topic">
          <el-select v-model="form.topic_index" placeholder="Select Topic" class="topic-select">
            <el-option
              v-for="item in topics"
              :key="item.id"
              :label="item['topic_' + lang]"
              :value="item.id">
            </el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer" :class="{ 'mobile-footer': isMobile }">
        <el-button @click="editCancel()" :class="{ 'mobile-button': isMobile }">取 消</el-button>
        <el-button type="primary" @click="editDone()" :class="{ 'mobile-button': isMobile }">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { formatDateTime } from '@/utils/timeUtil';

export default {
  name: "Quotes",
  data() {
    return {
      form: {},
      total: 0,
      pageNum: 1,
      pageSize: 10,
      quoteList: [],
      dialogFormVisible: false,
      searchTip: { quoteId: "", content: "", author: "" },
      lang: this.$route.params.lang,
      topics: [],
      isMobile: window.innerWidth <= 768
    }
  },
  created() {
    this.getQuotes()
    this.getTopics()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  mounted() {
    this.$watch('$route', (to, from) => {
      if (to.params.lang !== from.params.lang) {
        this.lang = to.params.lang
        this.pageNum = 1
        this.getQuotes()
      }
    })
  },
  methods: {
    handleResize() {
      this.isMobile = window.innerWidth <= 768
    },
    getQuotes() {
      this.request.get(`/motivo/admin/quotes/${this.lang}`, {
        params: {
          pageNum: this.pageNum,
          pageSize: this.pageSize,
          ...this.searchTip
        }
      }).then(res => {
        this.quoteList = res.quoteList.map(data => ({
          ...data,
          created_at: formatDateTime(data.created_at),
          updated_at: formatDateTime(data.updated_at),
        }))
        this.total = res.total
      }).catch(res => {
        this.$message.error(res?.message || "获取数据失败")
      })
    },
    getTopics() {
      this.request.get('/motivo/admin/topics/').then(res => {
        this.topics = res
      }).catch(res => {
        this.$message.error(res?.message || "获取Topics失败")
      })
    },
    handleSizeChange(pageSize) {
      this.pageSize = pageSize
      this.getQuotes()
    },
    handleCurrentChange(pageNum) {
      this.pageNum = pageNum
      this.getQuotes()
    },
    adding() {
      this.form = {}
      this.dialogFormVisible = true
    },
    editing(row) {
      this.form = row
      this.dialogFormVisible = true
    },
    editDone() {
      this.request.post(`/motivo/admin/quotes/${this.lang}/edit`, this.form).then(res => {
        if (res) {
          this.$message.success("添加成功")
          this.dialogFormVisible = false
          this.getQuotes()
        } else {
          this.$message.error("添加失败")
        }
      }).catch(res => {
        this.$message.error(res?.message || "添加失败")
      })
    },
    editCancel() {
      this.dialogFormVisible = false
    },
    deleting(quoteId) {
      this.request.delete(`/motivo/admin/quotes/${this.lang}/delete/` + quoteId).then(res => {
        if (res) {
          this.$message.success("删除成功")
          this.getQuotes()
        } else {
          this.$message.error("删除失败")
        }
      }).catch(res => {
        this.$message.error(res?.message || "删除失败")
      })
    }
  }
}
</script>

<style scoped>
.quotes-container {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.search-section {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
}

.table-container {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow-x: auto;
}

.custom-table {
  border-radius: 8px;
  overflow: hidden;
  min-width: 800px;
}

.custom-table ::v-deep .el-table__header-wrapper {
  background-color: #f5f7fa;
}

.custom-table ::v-deep .el-table__header th {
  background-color: #f5f7fa;
  color: #606266;
  font-weight: 600;
}

.custom-table ::v-deep .el-table__row:hover {
  background-color: #f5f7fa;
}

.expand-content {
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.expand-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
}

.expand-label {
  color: #606266;
  font-weight: 500;
  min-width: 100px;
}

.expand-value {
  color: #303133;
}

.content-cell, .author-cell {
  padding: 8px;
  line-height: 1.4;
  word-break: break-word;
}

.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.pagination-wrapper {
  margin-top: 20px;
  text-align: center;
}

.pagination-wrapper ::v-deep .el-pagination {
  white-space: nowrap;
  padding: 2px 5px;
  font-weight: 400;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 5px;
}

.headerBg {
  background: #eee!important;
}

.ugText {
  direction: rtl;
  text-align: right;
}

.normal {
  direction: ltr;
  text-align: left;
}

.el-table__row .cell {
  font-size: 14px;
  word-break: keep-all !important;
}

.el-input__inner {
  word-break: keep-all !important;
}

.el-textarea__inner {
  word-break: keep-all !important;
}

/* Mobile Dialog Styles */
.mobile-dialog ::v-deep .el-dialog {
  margin: 10vh auto !important;
  border-radius: 12px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.mobile-dialog ::v-deep .el-dialog__header {
  padding: 12px 15px;
  border-bottom: 1px solid #ebeef5;
}

.mobile-dialog ::v-deep .el-dialog__title {
  font-size: 16px;
  font-weight: 500;
  color: #303133;
}

.mobile-dialog ::v-deep .el-dialog__body {
  padding: 0;
  flex: 1;
  overflow-y: auto;
}

.edit-form {
  padding: 15px;
}

.edit-form ::v-deep .el-form-item {
  margin-bottom: 15px;
  display: flex;
  align-items: flex-start;
}

.edit-form ::v-deep .el-form-item__label {
  font-size: 14px;
  color: #606266;
  line-height: 36px;
  height: 36px;
  padding: 0 8px 0 0;
}

.edit-form ::v-deep .el-form-item__content {
  flex: 1;
  line-height: 36px;
  margin-left: 0 !important;
}

.edit-form ::v-deep .el-input__inner {
  height: 36px;
  font-size: 14px;
  padding: 0 12px;
  line-height: 36px;
}

.topic-select {
  width: 100%;
}

.mobile-footer {
  padding: 12px 15px;
  border-top: 1px solid #ebeef5;
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.mobile-button {
  flex: 1;
  height: 36px;
  font-size: 14px;
  border-radius: 6px;
  padding: 0 15px;
  line-height: 36px;
}

/* Media Queries */
@media screen and (min-width: 768px) {
  .quotes-container {
    padding: 20px;
  }

  .table-container {
    padding: 20px;
  }

  .expand-content {
    flex-direction: row;
    justify-content: space-around;
  }

  .mobile-dialog ::v-deep .el-dialog {
    margin: 15vh auto !important;
  }

  .edit-form ::v-deep .el-form-item__label {
    padding: 0 6px 0 0;
  }

  .mobile-button {
    height: 32px;
    line-height: 32px;
  }

  .topic-select {
    width: auto;
  }
}
</style>
